import { useState } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "@/mocks/site";
import { coreValues, contentPrinciples, aboutMilestones, testimonials } from "@/mocks/community";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daftbi9tto0ceqqnhh1g";

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [text, setText] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const honeypot = String(fd.get("contact_alt") || "").trim();

    // honeypot 命中：给与与成功相同的通用反馈，但不提交
    if (honeypot) {
      setStatus("ok");
      setFormError("");
      return;
    }
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "contact_alt" && value) payload.append(key, String(value));
    });

    setStatus("idle");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("ok");
        setFormError("");
        e.currentTarget.reset();
        setText("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎开小差了，请稍后再试。");
    }
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "AboutPage",
              "@id": `${siteConfig.siteUrl}/about`,
              url: `${siteConfig.siteUrl}/about`,
              name: "关于吃瓜日报",
              about: "吃瓜群众热点雷达与吃瓜指南",
              inLanguage: "zh-CN",
              mainEntityOfPage: { "@type": "WebPage", "@id": `${siteConfig.siteUrl}/about` },
            },
            {
              "@type": "ContactPage",
              url: `${siteConfig.siteUrl}/about#contact`,
              name: "联系与投稿",
              inLanguage: "zh-CN",
            },
          ],
        })}
      </script>

      {/* intro band */}
      <section className="border-b border-background-200 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-background-50 px-3 py-1 text-xs font-semibold text-primary-700">
              <i className="ri-water-flash-line text-sm" />
              关于吃瓜日报
            </span>
            <h1 className="mt-5 font-display text-3xl font-black leading-snug text-foreground-950 md:text-5xl">
              把乱成一锅粥的热点，
              <br className="hidden sm:block" />
              整理成<em className="not-italic text-accent-700">按节奏可开吃</em>的瓜。
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-foreground-700">
              {siteConfig.desc}
            </p>
          </div>
        </div>
      </section>

      {/* milestones / positioning */}
      <section className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">Our Story</p>
              <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">我们从哪儿来，要到哪儿去</h2>
              <div className="mt-6 space-y-5">
                {aboutMilestones.map((m) => (
                  <div key={m.year} className="flex gap-4">
                    <span className="flex h-10 w-14 shrink-0 items-center justify-center rounded-lg bg-accent-100 font-display text-sm font-black text-accent-800">
                      {m.year}
                    </span>
                    <p className="pt-2 text-sm leading-relaxed text-foreground-700">{m.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-700">Positioning</p>
              <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">我们的网站定位</h2>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {coreValues.map((v) => (
                  <div key={v.title} className="rounded-xl border border-background-200 bg-background-100/50 p-4">
                    <i className={`${v.icon} text-2xl text-primary-600`} />
                    <h3 className="mt-3 font-display text-base font-bold text-foreground-900">{v.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* principles */}
      <section className="border-b border-background-200 bg-foreground-950">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-400">Editorial Principles</p>
            <h2 className="mt-2 font-display text-2xl font-black text-background-50 md:text-3xl">内容原则，是吃瓜日报的底线</h2>
          </div>
          <ol className="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2">
            {contentPrinciples.map((p, i) => (
              <li key={p} className="flex items-start gap-3 rounded-xl border border-background-800 bg-background-900 p-4">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-500 font-display text-sm font-black text-background-50">
                  {i + 1}
                </span>
                <p className="text-sm leading-relaxed text-background-100">{p}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* testimonials */}
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-700">User Voices</p>
            <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">吃瓜群众说我们还不错</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5">
                <div className="flex items-center gap-1 text-primary-500">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <i key={k} className="ri-star-fill text-sm" />
                  ))}
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 font-display text-base font-bold text-accent-700">{t.name.slice(1, 2)}</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role} · {t.tag}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* contact form */}
      <section id="contact" className="scroll-mt-20 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">Contact & Submit</p>
              <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-3xl">
                投稿线索 / 联系编辑部
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                遇到想让我们理清楚的大瓜？或者对网站有建议？留下你的称呼、邮箱与线索说明即可。信息核实后，我们会把它纳入瓜单或复盘专栏，并在合理范围内标注你的来源。
              </p>
              <div className="mt-6 space-y-3 rounded-2xl border border-background-200 bg-background-100/60 p-5 text-sm">
                <p className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-mail-line text-primary-600" />
                  投稿邮箱：hi@melon-daily.example
                </p>
                <p className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-time-line text-primary-600" />
                  编辑时间：周一至周五 09:00 - 18:00
                </p>
                <p className="flex items-center gap-3 text-foreground-700">
                  <i className="ri-shield-check-line text-primary-600" />
                  承诺：不传播未经核实的隐私信息
                </p>
              </div>
              <div className="mt-5 flex items-center gap-2 text-xs text-foreground-500">
                <i className="ri-arrow-up-line" />
                顶部「投稿线索」按钮和页脚入口都能到达这里。
              </div>
            </div>

            <div className="lg:col-span-7">
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="f-name" className="mb-1.5 block text-sm font-medium text-foreground-800">怎么称呼你</label>
                    <input
                      id="f-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="例如：小栗子"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="f-email" className="mb-1.5 block text-sm font-medium text-foreground-800">你的联系邮箱</label>
                    <input
                      id="f-email"
                      name="email"
                      type="email"
                      required
                      maxLength={80}
                      placeholder="you@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label htmlFor="f-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">事件主题 / 所属专区</label>
                  <input
                    id="f-topic"
                    name="topic"
                    type="text"
                    required
                    maxLength={80}
                    placeholder="例如：综艺圈 · 恋综名场面争议"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div className="mt-4">
                  <label htmlFor="f-message" className="mb-1.5 flex items-center justify-between text-sm font-medium text-foreground-800">
                    线索 / 建议说明
                    <span className="text-xs font-normal text-foreground-400">{text.length}/500</span>
                  </label>
                  <textarea
                    id="f-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="把你掌握的时间线、线索或对网站的建议写在这里，越具体越好。"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                {/* honeypot */}
                <div className="honeypot-zone" aria-hidden="true">
                  <label htmlFor="contact_alt">请勿填写此栏</label>
                  <input
                    id="contact_alt"
                    name="contact_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-foreground-500">提交即表示你同意编辑部在核实后使用该线索（不含隐私信息）。</p>
                  <button
                    type="submit"
                    className="inline-flex whitespace-nowrap items-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                  >
                    <i className="ri-send-plane-line" />
                    提交线索
                  </button>
                </div>

                {status === "ok" && (
                  <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm font-medium text-accent-800">
                    <i className="ri-checkbox-circle-fill text-lg" />
                    已收到，感谢你的线索！编辑部会尽快跟进。
                  </p>
                )}
                {status === "error" && (
                  <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm font-medium text-primary-800">
                    <i className="ri-error-warning-line text-lg" />
                    {formError || "提交失败，请稍后再试。"}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}